import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { useAlertContext } from "../context/AlertContext";
import { useAuth } from "../context/AuthContext";
import { rolesForPath } from "../config/navConfig";
import {
    createEmployee,
    getEmployeesAccess,
    getEmployeesAdaptationSummary,
    getEmployeesTableContacts,
    getHiredEmployees,
    restoreEmployee,
    terminateEmployee,
} from "../services/hrOpsApi";
import FamilySection from "../components/employees/FamilySection";
import HiredEmployeeForm from "../components/employees/HiredEmployeeForm";
import EmployeeTerminationModal from "../components/employees/EmployeeTerminationModal";
import ConfigurableTable from "../components/table/ConfigurableTable";
import ColumnSettingsPanel from "../components/table/ColumnSettingsPanel";
import useTablePrefs from "../components/table/useTablePrefs";
import { emptyTablePrefs, visibleColumnIds, withColumns } from "../components/table/tablePrefs";
import { buildEmployeeColumns, DEFAULT_EMPLOYEE_COLUMNS, employeeMatch } from "../components/employees/employeeColumns";
import { AssetSearchResults, FillAssetsFromContactsButton } from "../components/employees/AssetSearchPanel";
import { searchAssets } from "../services/assetsApi";
import EmployeeFiltersPanel from "../components/employees/EmployeeFiltersPanel";
import { activeFilterCount, applyEmployeeFilters, filterSummary, withoutFilter } from "../components/employees/employeeFilters";
import { formatDateRu } from "../utils/dateFormat";

/**
 * «Сотрудники» — table with personal columns (Чат 4, правка №15). [Claude] 2026-11-02
 * Layout (columns, order, widths, sort) is stored per user on the server: ui-preferences "employees_table".
 * Former employees are a quiet secondary view («Уволенные»), relatives stay a separate tab.
 */
export default function UnifiedEmployeePage() {
    const navigate = useNavigate();
    const { showAlert } = useAlertContext();
    const { user } = useAuth();
    const canUseExcel = rolesForPath("/employees/excel").includes(String(user?.role || "").toLowerCase());
    const canManageAccess = rolesForPath("/employees/access").includes(String(user?.role || "").toLowerCase());
    const [section, setSection] = useState("employees");
    const [employees, setEmployees] = useState([]);
    const [adaptation, setAdaptation] = useState([]);
    const [access, setAccess] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [showTerminated, setShowTerminated] = useState(false);
    const [showColumns, setShowColumns] = useState(false);
    const [showFilters, setShowFilters] = useState(false); // фильтры таблицы (правка №15, MR 4)
    // Контакты для колонок и общего поиска; поиск по технике, номерам и аккаунтам (№33) — с сервера (доработка №15, 04.11).
    const [contactsByEmployee, setContactsByEmployee] = useState(() => new Map());
    const [remote, setRemote] = useState({ q: "", assets: [], people: [] });
    const remoteRef = useRef(0);
    const [showCreate, setShowCreate] = useState(false);
    const [creating, setCreating] = useState(false);
    const [terminating, setTerminating] = useState(null);
    const [terminationLoading, setTerminationLoading] = useState(false);
    const [restoringId, setRestoringId] = useState(null);
    const requestRef = useRef(0);
    const { prefs, update: updatePrefs, saveError } = useTablePrefs("employees_table");

    const load = async () => {
        const requestId = ++requestRef.current;
        setLoading(true);
        try {
            const result = await getHiredEmployees(null, { activeOnly: false });
            if (requestId === requestRef.current) setEmployees(Array.isArray(result) ? result : []);
        } catch (error) {
            if (requestId === requestRef.current) {
                setEmployees([]);
                showAlert(`Не удалось загрузить сотрудников: ${error.message || error}`, "error");
            }
        } finally {
            if (requestId === requestRef.current) setLoading(false);
        }
        // What the user may see (pay column) — optional too.
        Promise.resolve().then(() => getEmployeesAccess())
            .then((value) => { if (requestId === requestRef.current) setAccess(value || null); })
            .catch(() => setAccess(null));
        // Contacts of «Контакты» (phones / e-mail / Telegram with their usage) — optional too.
        Promise.resolve().then(() => getEmployeesTableContacts())
            .then((rows) => { if (requestId === requestRef.current) setContactsByEmployee(new Map((Array.isArray(rows) ? rows : []).map((r) => [r.employee_id, r.contacts || []]))); })
            .catch(() => {});
        // Adaptation column is optional: the table works without it.
        try {
            const summary = await getEmployeesAdaptationSummary();
            if (requestId === requestRef.current) setAdaptation(Array.isArray(summary) ? summary : []);
        } catch {
            if (requestId === requestRef.current) setAdaptation([]);
        }
    };

    useEffect(() => { void load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (saveError) showAlert(`Настройка таблицы не сохранилась: ${saveError}`, "error");
    }, [saveError]); // eslint-disable-line react-hooks/exhaustive-deps

    // One search box finds people by name, any phone / e-mail / Telegram and things by number, login or inv. №.
    useEffect(() => {
        const q = search.trim();
        const id = ++remoteRef.current;
        if (q.length < 3) { setRemote({ q: "", assets: [], people: [] }); return undefined; }
        const timer = setTimeout(() => {
            Promise.resolve().then(() => searchAssets(q))
                .then((res) => { if (id === remoteRef.current) setRemote({ q, assets: res?.assets || [], people: res?.people || [] }); })
                .catch(() => { if (id === remoteRef.current) setRemote({ q, assets: [], people: [] }); });
        }, 350);
        return () => clearTimeout(timer);
    }, [search]);

    const openEmployee = (employee) => navigate(`/employees/${employee.id}`);
    const adaptationByEmployee = useMemo(() => new Map(adaptation.map((row) => [row.employee_id, row])), [adaptation]);
    const canSeeSalary = Boolean(access?.salary_scope);
    const catalog = useMemo(() => buildEmployeeColumns({ adaptationByEmployee, contactsByEmployee, onOpen: openEmployee, canSeeSalary }), [adaptationByEmployee, contactsByEmployee, canSeeSalary]); // eslint-disable-line react-hooks/exhaustive-deps
    const visibleIds = visibleColumnIds(catalog, prefs, DEFAULT_EMPLOYEE_COLUMNS);

    // In «Уволенные» the dismissal date and reason are always shown (if the user did not add them himself).
    const extraColumns = useMemo(() => (showTerminated
        ? catalog.filter((c) => ["date_fired", "termination_reason"].includes(c.id) && !visibleIds.includes(c.id))
        : []), [showTerminated, catalog, visibleIds.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

    const counts = useMemo(() => ({
        active: employees.filter((e) => !e.date_fired).length,
        terminated: employees.filter((e) => e.date_fired).length,
    }), [employees]);

    // Last filters of the user live in the table layout on the server (prefs.filters).
    const filters = useMemo(() => prefs?.filters || {}, [prefs]);
    const setFilters = (next) => updatePrefs({ ...prefs, filters: next });
    const filterCount = activeFilterCount(filters);
    const chips = filterSummary(filters);

    // Search works as a filter over everybody (fired included, marked): only matching rows stay, and the row
    // says what matched — «найдено: тел. (раб.) +7 …» (доработка №15, 04.11). [Claude] 2026-11-04
    const query = search.trim();
    const remotePeople = useMemo(() => (remote.q === query ? remote.people : []), [remote, query]);
    const visible = useMemo(() => {
        if (!query) {
            return applyEmployeeFilters(employees.filter((e) => showTerminated === Boolean(e.date_fired)), filters, { adaptationByEmployee });
        }
        const server = new Map(remotePeople.map((p) => [p.employee_id, p]));
        const rows = [];
        employees.forEach((employee) => {
            const local = employeeMatch(employee, contactsByEmployee.get(employee.id) || [], query);
            const found = server.get(employee.id);
            if (!local && !found) return;
            const hint = local?.field ? local : found?.matches?.[0] ? { field: found.matches[0].field, value: found.matches[0].value } : { field: null };
            rows.push({ ...employee, _match: hint });
        });
        rows.sort((a, b) => Number(Boolean(a.date_fired)) - Number(Boolean(b.date_fired)));
        return applyEmployeeFilters(rows, filters, { adaptationByEmployee });
    }, [employees, query, showTerminated, filters, adaptationByEmployee, contactsByEmployee, remotePeople]);
    const remoteAssets = remote.q === query ? remote.assets : [];
    const extraPeople = useMemo(() => remotePeople.filter((p) => !employees.some((e) => e.id === p.employee_id)), [remotePeople, employees]);

    const create = async (payload) => {
        setCreating(true);
        try {
            const employee = await createEmployee(payload);
            setEmployees((current) => [employee, ...current]);
            setShowCreate(false);
            const password = employee?.erp_generated_password;
            showAlert(password
                ? `Сотрудник и учётка ERP созданы. Пароль ERP: ${password}`
                : payload.create_erp_account
                    ? "Сотрудник и учётка ERP созданы"
                    : "Сотрудник добавлен в HR-платформу", "success");
        } catch (error) {
            showAlert(error.message || String(error), "error");
            throw error;
        } finally {
            setCreating(false);
        }
    };

    const terminate = async (payload) => {
        setTerminationLoading(true);
        try {
            const updated = await terminateEmployee(terminating.id, payload);
            setEmployees((current) => current.map((item) => item.id === updated.id ? updated : item));
            setTerminating(null);
            showAlert(updated.erp_user_id
                ? "Сотрудник уволен, доступ к ERP отключён"
                : "Увольнение оформлено, дата и причина сохранены", "success");
        } catch (error) {
            showAlert(error.message || String(error), "error");
        } finally {
            setTerminationLoading(false);
        }
    };

    const restore = async (employee) => {
        if (!window.confirm(`Вернуть сотрудника «${employee.full_name}» в действующие? Связанная учётная запись ERP будет активирована.`)) return;
        setRestoringId(employee.id);
        try {
            const updated = await restoreEmployee(employee.id);
            setEmployees((current) => current.map((item) => item.id === updated.id ? updated : item));
            showAlert(updated.erp_user_id ? "Сотрудник восстановлен, доступ к ERP активирован" : "Сотрудник восстановлен", "success");
        } catch (error) {
            showAlert(error.message || String(error), "error");
        } finally {
            setRestoringId(null);
        }
    };

    const actions = (employee) => (!employee.date_fired
        ? <button type="button" onClick={() => setTerminating(employee)} className="text-xs font-semibold text-slate-400 opacity-0 transition hover:text-red-700 group-hover:opacity-100 focus:opacity-100" data-testid={`terminate-employee-${employee.id}`}>Уволить</button>
        : <button type="button" disabled={restoringId === employee.id} onClick={() => restore(employee)} className="text-xs font-bold text-emerald-700 hover:text-emerald-900 disabled:opacity-50" data-testid={`restore-employee-${employee.id}`} title={`Уволен ${formatDateRu(employee.date_fired)}`}>{restoringId === employee.id ? "Восстановление…" : "Вернуть в сотрудники"}</button>);

    const tabClass = (active) => `rounded-lg px-4 py-1.5 text-sm font-semibold ${active ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`;

    return <MainLayout>
        <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-2xl font-bold text-slate-900">Сотрудники</h1>
                <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1" data-testid="hr-platform-subtabs">
                    <button type="button" onClick={() => setSection("employees")} className={tabClass(section === "employees")} data-testid="tab-all">Сотрудники <span className="opacity-60">{counts.active}</span></button>
                    <button type="button" onClick={() => setSection("families")} className={tabClass(section === "families")} data-testid="families-subtab">Родственники</button>
                </div>
            </div>
            {section === "employees" ? <div className="flex flex-wrap gap-2">
                {canUseExcel ? <button type="button" onClick={() => navigate("/employees/excel")} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="employee-excel-center">Excel</button> : null}
                {canManageAccess ? <button type="button" onClick={() => navigate("/employees/access")} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="employee-access">Доступы</button> : null}
                <button type="button" onClick={() => setShowCreate(true)} className="rounded-lg bg-amber-400 px-4 py-1.5 text-sm font-bold text-slate-900 hover:bg-amber-300" data-testid="add-hired-employee">+ Добавить сотрудника</button>
            </div> : null}
        </header>

        {section === "families" ? <FamilySection employees={employees.filter((item) => !item.date_fired)} /> : <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-4 py-2.5">
                <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Поиск: ФИО, отдел, должность, любой телефон, почта, Telegram, логин, инв. номер" className="min-w-[260px] flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm outline-none focus:border-amber-400 focus:bg-white" data-testid="all-employees-search" />
                <span className="text-sm text-slate-500">{showTerminated && !query ? "Уволенные" : "Найдено"}: <strong className="text-slate-900" data-testid="employees-found">{visible.length}</strong></span>
                <button type="button" onClick={() => setShowFilters((v) => !v)} aria-pressed={showFilters} className={`rounded-lg border px-3 py-1.5 text-sm font-semibold ${showFilters || filterCount ? "border-amber-300 bg-amber-50 text-amber-900" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`} data-testid="employee-filters-button">Фильтры{filterCount ? ` · ${filterCount}` : ""}</button>
                <button type="button" onClick={() => setShowColumns(true)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="employee-columns-button">Колонки</button>
                <button type="button" onClick={() => setShowTerminated((v) => !v)} className="text-xs text-slate-400 underline-offset-2 hover:text-slate-700 hover:underline" data-testid="show-terminated-employees">
                    {showTerminated ? "← Действующие" : `Уволенные (${counts.terminated})`}
                </button>
                {canUseExcel ? <FillAssetsFromContactsButton /> : null}
            </div>
            {showFilters ? <EmployeeFiltersPanel rows={employees.filter((e) => showTerminated === Boolean(e.date_fired))} filters={filters} onChange={setFilters} onReset={() => setFilters({})} /> : null}
            {chips.length ? <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 px-4 py-2" data-testid="employee-filter-chips">
                {chips.map((chip) => <span key={chip.key} className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-800">{chip.text}<button type="button" onClick={() => setFilters(withoutFilter(filters, chip.key))} className="text-amber-500 hover:text-amber-900" aria-label={`Убрать фильтр «${chip.text}»`}>×</button></span>)}
                <button type="button" onClick={() => setFilters({})} className="text-xs text-slate-400 hover:text-slate-700" data-testid="employee-filters-clear">Сбросить</button>
            </div> : null}
            {query ? <AssetSearchResults assets={remoteAssets} people={extraPeople} onOpenEmployee={(id) => navigate(`/employees/${id}`)} /> : null}
            {loading ? <p className="py-12 text-center text-slate-400">Загрузка сотрудников…</p> : <ConfigurableTable
                testId="all-employees-table"
                catalog={catalog}
                defaultColumns={DEFAULT_EMPLOYEE_COLUMNS}
                rows={visible}
                prefs={prefs}
                onPrefsChange={updatePrefs}
                onRowClick={openEmployee}
                extraColumns={extraColumns}
                actions={actions}
                empty={search || filterCount ? "По вашему запросу ничего не найдено" : showTerminated ? "Уволенных нет" : "Сотрудников пока нет"}
            />}
        </section>}

        {showColumns ? <ColumnSettingsPanel
            catalog={catalog}
            visibleIds={visibleIds}
            onChange={(ids) => updatePrefs(withColumns(prefs, catalog, ids))}
            onReset={() => updatePrefs({ ...emptyTablePrefs(), filters })}
            onClose={() => setShowColumns(false)}
        /> : null}
        {showCreate ? <HiredEmployeeForm onClose={() => setShowCreate(false)} onSubmit={create} isLoading={creating} /> : null}
        {terminating ? <EmployeeTerminationModal employee={terminating} loading={terminationLoading} onCancel={() => setTerminating(null)} onConfirm={terminate} /> : null}
    </MainLayout>;
}
